import {Injectable} from "@angular/core";
import {EExerciseType} from "../../shared/enums/EExerciseType";
import {IExerciseConfig} from "../../features/exercises/exercise-form/exercise-form.component";
import {ECountdownEndType} from "../../shared/enums/ECountdownEndType";
import {EExerciseJumpType} from "../../shared/enums/EExerciseJumpType";
import {EAgilityCustomType} from "../../shared/enums/EAgilityCustomType";
import {ESumType} from "../../shared/enums/ESumType";
import {EStroopEffectType} from "../../shared/enums/EStroopEffectType";
import {ECompanionMode} from "../../shared/enums/ECompanionMode";
import {ECompanionSimultaneousType} from "../../shared/enums/ECompanionSimultaneousType";
import {ECompanionCommunicationType} from "../../shared/enums/ECompanionCommunicationType";
import {ECompanionAgilityType} from "../../shared/enums/ECompanionAgilityType";

@Injectable({providedIn: 'root'})
export class ExerciseHelper {
    getConfigTitle(exerciseType: EExerciseType, exerciseConfig: IExerciseConfig): string {
        if(!exerciseConfig.timerPrompt) exerciseConfig.timerPrompt = 0;
        if(exerciseType === EExerciseType.Count) {
            let countText;
            if(exerciseConfig.sumType === ESumType.SUM_ALL) countText = "Sum All";
            else countText = "Left Right";
            if(exerciseConfig.countdownEndType === ECountdownEndType.TOUCHES) {
                return `${exerciseConfig.countdownEnd} Touches, ${countText} Count`;
            } else if(exerciseConfig.countdownEndType === ECountdownEndType.TIME) {
                return `${exerciseConfig.countdownEnd} Seconds, ${countText} Count`;
            }
        } else if(exerciseType === EExerciseType.React) {
            let value = "";
            if(exerciseConfig.countdownEndType === ECountdownEndType.TOUCHES) {
                value =  `${exerciseConfig.countdownEnd} Touches, ${exerciseConfig.timerDelay} Delay, ${exerciseConfig.timerPrompt} Flash`;
            } else if(exerciseConfig.countdownEndType === ECountdownEndType.TIME) {
                value = `${exerciseConfig.countdownEnd} Seconds, ${exerciseConfig.timerDelay} Delay, ${exerciseConfig.timerPrompt} Flash`;
            }
            if(exerciseConfig.stroopEffectType === EStroopEffectType.CONGRUENT) value += ", Congruent Stroop";
            else if(exerciseConfig.stroopEffectType === EStroopEffectType.INCONGRUENT) value += ", Incongruent Stroop";
            if(exerciseConfig.footFireActivity)  value += ", Foot Fire";
            if(exerciseConfig.liftTime) value += ", Lift Time";
            return value;
        } else if(exerciseType === EExerciseType.Sequence) {
            if(exerciseConfig.countdownEndType === ECountdownEndType.TOUCHES) {
                return `${exerciseConfig.countdownEnd} Touches, Sequence ${exerciseConfig.sensorsSequence.join('')}, ${exerciseConfig.timerDelay} Delay, ${exerciseConfig.timerPrompt} Flash`;
            } else if(exerciseConfig.countdownEndType === ECountdownEndType.TIME) {
                return `${exerciseConfig.countdownEnd} Seconds, Sequence ${exerciseConfig.sensorsSequence.join('')}, ${exerciseConfig.timerDelay} Delay, ${exerciseConfig.timerPrompt} Flash`;
            }
        } else if(exerciseType === EExerciseType.Agility) {
            if(exerciseConfig.countdownEndType === ECountdownEndType.TOUCHES) {
                if(exerciseConfig.arrowsType === EAgilityCustomType.Sequence)
                    return `${exerciseConfig.countdownEnd} Arrows, ${exerciseConfig.timerDelay} Delay, ${exerciseConfig.timerPrompt} Flash, Agility Sequence: ${this.getArrows(exerciseConfig.arrows)}`;
                else if(exerciseConfig.arrowsType === EAgilityCustomType.React)
                    return `${exerciseConfig.countdownEnd} Arrows, ${exerciseConfig.timerDelay} Delay, ${exerciseConfig.timerPrompt} Flash, Agility React: ${this.getArrows(exerciseConfig.arrows)}`;
            } else if(exerciseConfig.countdownEndType === ECountdownEndType.TIME) {
                if(exerciseConfig.arrowsType === EAgilityCustomType.Sequence)
                    return `${exerciseConfig.countdownEnd} Seconds, ${exerciseConfig.timerDelay} Delay, ${exerciseConfig.timerPrompt} Flash, Agility Sequence: ${this.getArrows(exerciseConfig.arrows)}`;
                else if(exerciseConfig.arrowsType === EAgilityCustomType.React)
                    return `${exerciseConfig.countdownEnd} Seconds, ${exerciseConfig.timerDelay} Delay, ${exerciseConfig.timerPrompt} Flash, Agility React: ${this.getArrows(exerciseConfig.arrows)}`;
            }
        } else if(exerciseType === EExerciseType.Vertical) {
            return `${exerciseConfig.jumpType === EExerciseJumpType.DROP_JUMP ? 'Drop Jump' : 'Vertical Jump'}, ${exerciseConfig.jumpCount} Jump(s), ${exerciseConfig.numberOfLegs} Leg(s)`;
        } else if(exerciseType === EExerciseType.Companion) {
            // Build the companion title following the Swift code structure
            const companionsCount = exerciseConfig.companionsCount ?? 0;
            const sensorsCount = exerciseConfig.sensorsCount ?? 0;
            const mode = exerciseConfig.modeType ?? ECompanionMode.Simultaneous;
            
            // Format companions and sensors text
            const companionsText = `${companionsCount} Companion${companionsCount === 1 ? '' : 's'}`;
            const sensorsText = `${sensorsCount} Sensor${sensorsCount === 1 ? '' : 's'}`;
            
            // Get mode name
            const modeText = this.getCompanionModeName(mode);
            
            // Get detail text based on mode
            const detailText = this.getCompanionDetailText(mode, exerciseConfig);
            
            return `${companionsText}, ${sensorsText}, ${modeText}, ${detailText}`;
        }
        return ''
    }

    private getCompanionModeName(mode: ECompanionMode): string {
        switch (mode) {
            case ECompanionMode.Simultaneous:
                return "Simultaneous";
            case ECompanionMode.React:
                return "React";
            case ECompanionMode.Agility:
                return "Agility";
            default:
                return "Unknown";
        }
    }

    private getCompanionDetailText(mode: ECompanionMode, exerciseConfig: IExerciseConfig): string {
        switch (mode) {
            case ECompanionMode.Simultaneous:
                const simultaneousType = exerciseConfig.simultaneousType ?? ECompanionSimultaneousType.Count;
                return this.getSimultaneousTypeName(simultaneousType);
            case ECompanionMode.React:
                const reactCommunicationType = exerciseConfig.reactCommunicationType ?? ECompanionCommunicationType.Alternate;
                return this.getReactCommunicationTypeName(reactCommunicationType);
            case ECompanionMode.Agility:
                const agilityType = exerciseConfig.agilityType ?? ECompanionAgilityType.Color;
                return this.getAgilityTypeName(agilityType);
            default:
                return "Unknown";
        }
    }

    private getSimultaneousTypeName(type: ECompanionSimultaneousType): string {
        switch (type) {
            case ECompanionSimultaneousType.Count:
                return "Count";
            case ECompanionSimultaneousType.Sequence:
                return "Sequence";
            default:
                return "Unknown";
        }
    }

    private getReactCommunicationTypeName(type: ECompanionCommunicationType): string {
        switch (type) {
            case ECompanionCommunicationType.Alternate:
                return "Alternate";
            case ECompanionCommunicationType.Random:
                return "Random";
            default:
                return "Unknown";
        }
    }

    private getAgilityTypeName(type: ECompanionAgilityType): string {
        switch (type) {
            case ECompanionAgilityType.Arrow:
                return "Arrow";
            case ECompanionAgilityType.Color:
                return "Color";
            case ECompanionAgilityType.Shape:
                return "Shape";
            case ECompanionAgilityType.Number:
                return "Number";
            default:
                return "Unknown";
        }
    }

    getArrows(sensorArray: number[]): string {
        const arrows: { [key: number]: string } = {
            1: '↖️',  // TopLeft
            2: '⬆️',  // TopCenter
            3: '↗️',  // TopRight
            4: '⬅️',  // MiddleLeft
            5: '⏹️',  // MiddleCenter
            6: '➡️',  // MiddleRight
            7: '↙️',  // BottomLeft
            8: '⬇️',  // BottomCenter
            9: '↘️'   // BottomRight
        };

        return sensorArray.map(sensor => arrows[sensor] || '?').join('');
    }

}
